import { Box, Text, VStack, Icon, Divider, Flex } from '@chakra-ui/react';
import OrderNotificationIcon from '../../../assets/svg/notifications/order-icon.svg?react';
import { MdOutlineInventory2 } from 'react-icons/md';
import { MdPayment } from 'react-icons/md';
import { FcAdvertising } from 'react-icons/fc';
import emptyNotification from '../../../assets/svg/notifications/empty-notification.svg?react';
import { usePatchLatestNotification } from '@/services/notifications';
import { ElementType, useEffect } from 'react';

import useNotifications from '@/hooks/useNotification';

interface Notification {
  id: number;
  message: string;
  is_read: boolean;
}

interface NotificationListProps {
  items?: Notification[];
  category: 'Order' | 'Inventory' | 'Payment' | 'Promotion';
  isActive?: boolean;
}

const NotificationList: React.FC<NotificationListProps> = ({ items, category, isActive }) => {
  const { mutateAsync: patchLatestNotificationCategory } = usePatchLatestNotification();
  const { markReadNotification } = useNotifications();

  useEffect(() => {
    if (isActive && category) {
      patchLatestNotificationCategory(category);
    }
  }, [isActive, category, patchLatestNotificationCategory]);

  if (!items || items.length === 0) {
    return (
      <Box
        height="400px"
        width="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderBottomRadius={'xl'}
      >
        <VStack spacing={2}>
          <Icon as={emptyNotification} boxSize={40} />
          <VStack spacing={1}>
            <Text color={'Text.primary'} fontSize={'xl'} fontWeight={'semibold'}>
              No Notification Yet
            </Text>
            <Text color={'text.disable'} fontSize={'sm'}>
              We will update you when you get any notification
            </Text>
          </VStack>
        </VStack>
      </Box>
    );
  }

  const getCategoryIcon = (category: string): ElementType | undefined => {
    switch (category) {
      case 'Order':
        return OrderNotificationIcon as ElementType;
      case 'Inventory':
        return MdOutlineInventory2;
      case 'Payment':
        return MdPayment;
      case 'Promotion':
        return FcAdvertising;
      default:
        return undefined;
    }
  };

  const IconComponent = getCategoryIcon(category);

  return (
    <VStack
      align="start"
      width="100%"
      spacing={0}
      minHeight={'400px'}
      maxHeight={'400px'}
      overflowY={'auto'}
      sx={{
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#EC7E7E',
          borderRadius: '4px',
          border: '2px solid #fff',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '4px',
        },
      }}
    >
      {items.map((item) => (
        <Box
          as="button"
          key={item.id} // Stable key using ID
          width="100%"
          bg={item.is_read ? 'white' : 'gray.100'}
          onClick={() => {
            markReadNotification(item.id, category); // Pass category for targeted update
          }}
          cursor={'pointer'}
          _focusVisible={{
            outline: 'none',
            border: '1px solid',
            borderColor: '#A71C29',
          }}
        >
          <Flex align="center" px={4} py={4}>
            <Box
              bg="#FEA01A1F"
              borderRadius="full"
              p={4}
              mr={4}
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="40px"
              height="40px"
            >
              <Icon as={IconComponent} color={'brand.secondary.DEFAULT'} boxSize={5} />
            </Box>
            <Text
              fontSize="sm"
              flex="1"
              display="flex"
              alignItems="center"
              textAlign={'left'}
              noOfLines={2}
            >
              {item.message || 'New notification'}
            </Text>
          </Flex>
          <Divider orientation="horizontal" width="100%" borderColor={'border.default'} />
        </Box>
      ))}
    </VStack>
  );
};

export default NotificationList;
